/**
 * Remembers the page a logged-out visitor asked for, so sign-in can return there instead of
 * /dashboard. Kept in sessionStorage, which survives the same-tab SSO round trip
 * (/oauth2/start -> Entra -> /sso-callback).
 */
const KEY = 'ikompete.returnTo';

/** Pages that are part of signing in; returning to them would loop. */
const SIGN_IN_PATHS = ['/login', '/sso-callback'];

/** Control characters, whitespace or a backslash: browsers' URL parsing strips or rewrites these. */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\u0000-\u001f\u007f\s\\]/;

/**
 * Normalises a saved path, or returns null unless it is a same-site path ("/x", never "//host",
 * "/\\host", "/\t/host" or "https://host") outside the sign-in pages.
 */
function safePath(path: string): string | null {
  if (!path.startsWith('/') || path.startsWith('//') || UNSAFE_CHARS.test(path)) return null;
  let url: URL;
  try {
    url = new URL(path, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin || SIGN_IN_PATHS.includes(url.pathname)) return null;
  // "/.//host" and "/%2e%2e//host" normalise to "//host", which a later navigation reads as a host.
  if (url.pathname.startsWith('//')) return null;
  return url.pathname + url.search + url.hash;
}

/** Remembers a same-site path; unsafe and sign-in paths are ignored (any earlier path stays). */
export function saveReturnTo(path: string): void {
  if (safePath(path) === null) return;
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    // Storage unavailable: sign-in falls back to /dashboard.
  }
}

/** Reads and clears the saved path; null when nothing usable is saved. */
export function takeReturnTo(): string | null {
  let path: string | null = null;
  try {
    path = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  return path === null ? null : safePath(path);
}

/** Forgets any saved path (on sign-out). */
export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}
